import { ErrorMessage } from '@hookform/error-message';
import { Autocomplete, FormControl, Grid, TextField } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField/TextField';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment/moment';
import { useCallback, useMemo } from 'react';
import * as React from 'react';
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
  useController,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Employees } from '~/data/employees/types';
import { OrgUnitEmployeeRole, ResponsibleEmps } from '~/data/orgunits';
import { useVisible } from '~/hooks/useVisible';
import { OrgUnitFormData } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup';
import { OrgUnitSectionWrapper } from '~/scenes/PortalViews/Views/PortalAltMgr/components/OrgUnitPopup/components/OrgUnitSectionWrapper';

type PropsType = {
  form: UseFormReturn<OrgUnitFormData>;
  allEmployees: Employees[];
  disabled?: boolean;
  commonTextFieldProps?: Partial<TextFieldProps>;
};

export const OrgUnitSubstituteManagerFormSection = (props: PropsType) => {
  const { form, allEmployees, disabled, commonTextFieldProps } = props;
  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = form;

  const { t } = useTranslation();

  const substituteOptions: ResponsibleEmps[] = allEmployees.map((employee) => {
    return {
      fEmployeeId: employee.fEmpId,
      fUnitRole: OrgUnitEmployeeRole.SubstituteAbsenceManager,
    };
  });

  const getSubstituteOptionLabel = useCallback(
    (empOption: ResponsibleEmps) => {
      const employee = allEmployees.find(
        (employee) => employee.fEmpId === empOption.fEmployeeId,
      );

      // TODO Handle not found message
      return !!employee
        ? `${employee.fEmpFirstName} ${employee.fEmpLastName}, ${employee.fEmpPersonNumber}`
        : '-';
    },
    [allEmployees],
  );

  const hasSubstituteManager = !!watch('substituteManager');

  const {
    field: { value: startValueString, onChange: onStartChange, ...startField },
  } = useController({
    name: 'substituteStartDate',
    control: control,
  });

  const {
    field: { value: endValueString, onChange: onEndChange, ...endField },
  } = useController({
    name: 'substituteEndDate',
    control: control,
  });

  const values: DateRange<Moment> = useMemo(() => {
    const stringToDateOrNull = (value: string) =>
      value ? moment(value as string, 'YYYY-MM-DD') : null;

    return [
      stringToDateOrNull(startValueString),
      stringToDateOrNull(endValueString),
    ];
  }, [startValueString, endValueString]);

  const handleRangeChange = useCallback(
    (dateRange: DateRange<Moment | null>) => {
      const [start, end] = dateRange;
      const [startValue, endValue] = values;

      const dateToString = (date: Moment | null) =>
        date ? moment(date).format('YYYY-MM-DD') : '';

      if (start !== startValue) {
        onStartChange(dateToString(start));
      }
      if (end !== endValue) {
        onEndChange(dateToString(end));
      }
    },
    [values, onStartChange, onEndChange],
  );

  const resetDates = useCallback(
    (newValue?: ResponsibleEmps | null) => {
      const dateFieldsHaveValue = !!values.find((v) => v !== null);

      const noManager =
        typeof newValue !== 'undefined' ? !newValue : !hasSubstituteManager;

      if (noManager && dateFieldsHaveValue) {
        setValue('substituteStartDate', '');
        setValue('substituteEndDate', '');
      }
    },
    [values, hasSubstituteManager, setValue],
  );

  const getTextFieldProps = (
    fieldProps: Omit<
      ControllerRenderProps<OrgUnitFormData>,
      'value' | 'onChange'
    >,
    label: string,
  ): TextFieldProps => {
    const hasFieldError = errors.hasOwnProperty(fieldProps.name);

    return {
      ...fieldProps,
      label,
      required: hasSubstituteManager,
      fullWidth: true,
      error: hasFieldError,
      helperText: hasFieldError && (
        <ErrorMessage errors={errors} name={fieldProps.name} />
      ),
      ...commonTextFieldProps,
    };
  };

  const optionsVisibility = useVisible(false);
  const toggleOptionsVisibility = useCallback(
    (inputValue: string, value: ResponsibleEmps | null) => {
      if (
        inputValue.length > 2 &&
        (!value || inputValue !== getSubstituteOptionLabel(value))
      ) {
        optionsVisibility.show();
      } else {
        optionsVisibility.hide();
      }
    },
    [optionsVisibility, getSubstituteOptionLabel],
  );

  return (
    <Grid item xs={12}>
      <OrgUnitSectionWrapper>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={6}>
            <Controller
              control={control}
              name="substituteManager"
              render={({ field: { onChange, value, ...field } }) => (
                <FormControl fullWidth>
                  <Autocomplete
                    {...field}
                    options={substituteOptions}
                    getOptionLabel={getSubstituteOptionLabel}
                    renderOption={(props, option, index) => {
                      return (
                        <li
                          {...props}
                          key={`substitute-option-${index}-${option.fEmployeeId}`}
                        >
                          {getSubstituteOptionLabel(option)}
                        </li>
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option.fEmployeeId === value?.fEmployeeId
                    }
                    value={value}
                    onChange={(_, newValue) => {
                      onChange(newValue);
                      optionsVisibility.hide();
                      resetDates(newValue);
                    }}
                    disabled={disabled}
                    onInputChange={(_event, inputValue) => {
                      toggleOptionsVisibility(inputValue, value);
                    }}
                    open={optionsVisibility.isVisible}
                    forcePopupIcon={false}
                    onBlur={() => resetDates()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('fOrgUnitSubstituteManager')}
                        {...commonTextFieldProps}
                      />
                    )}
                  />
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <DateRangePicker
              value={values}
              onChange={handleRangeChange}
              disabled={disabled || !hasSubstituteManager}
              slotProps={{
                fieldRoot: {
                  sx: {
                    // Reduce spacing around separator
                    '& > :not(style) ~ :not(style)': {
                      marginLeft: '4px',
                    },
                  },
                },
                textField: ({ position }) => {
                  switch (position) {
                    case 'start':
                      return getTextFieldProps(startField, t('startDate'));
                    case 'end':
                      return getTextFieldProps(endField, t('endDate'));
                    default:
                      return {};
                  }
                },
              }}
            />
          </Grid>
        </Grid>
      </OrgUnitSectionWrapper>
    </Grid>
  );
};
